/**
 * Prompt Engineering Service for Classification
 * 
 * This service provides prompt templates and construction logic for accurate
 * prompt classification using the ministral-3b model.
 * 
 * Features:
 * - System prompt templates optimized for classification
 * - User prompt construction with sanitization
 * - Response parsing and validation
 * - Confidence scoring mechanisms
 * - Dynamic category integration with CategoryLoader
 * - Enhanced caching and health monitoring
 * - Advanced template variable replacement system
 * - Intelligent prompt construction and truncation
 */

import { CategoryLoader, CategoryLogger } from './category-loader';
import type { Category } from '@/types/categories';

/**
 * Enhanced classification prompt request interface with advanced features
 */
export interface ClassificationPromptRequest {
  /** The user prompt text to classify */
  promptText: string;
  /** Optional context or additional information */
  context?: string;
  /** Maximum length for prompt truncation */
  maxLength?: number;
  /** Force refresh of categories from CategoryLoader */
  forceRefresh?: boolean;
  /** Prompt complexity level for different handling strategies */
  complexityLevel?: 'simple' | 'medium' | 'complex';
  /** Template variables for dynamic prompt construction */
  templateVariables?: Record<string, string>;
  /** Context type for enhanced formatting */
  contextType?: 'general' | 'technical' | 'creative' | 'educational';
  /** Additional metadata to include in prompt */
  metadata?: {
    /** Source of the prompt (e.g., 'api', 'ui', 'batch') */
    source?: string;
    /** User intent if known */
    intent?: string;
    /** Priority level */
    priority?: 'high' | 'medium' | 'low';
  };
}

/**
 * Enhanced user prompt construction options
 */
export interface UserPromptOptions {
  /** Template to use for prompt construction */
  template?: 'standard' | 'detailed' | 'concise' | 'technical';
  /** Whether to include examples in prompt */
  includeExamples?: boolean;
  /** Truncation strategy */
  truncationStrategy?: 'simple' | 'intelligent' | 'preserve_context';
  /** Context formatting style */
  contextFormatting?: 'inline' | 'structured' | 'minimal';
  /** Variable replacement options */
  variableReplacement?: {
    /** Whether to escape variables for safety */
    escapeVariables?: boolean;
    /** Default values for missing variables */
    defaults?: Record<string, string>;
  };
}

/**
 * Classification prompt response interface
 */
export interface ClassificationPromptResponse {
  /** Complete system prompt */
  systemPrompt: string;
  /** Complete user prompt */
  userPrompt: string;
  /** Total token estimate */
  estimatedTokens: number;
  /** Available categories for this classification */
  availableCategories: Category[];
  /** Metadata about category loading */
  categoryMetadata: {
    /** Total number of categories loaded */
    totalCategories: number;
    /** Whether categories were loaded from cache */
    fromCache: boolean;
    /** Cache age in milliseconds */
    cacheAge: number;
    /** Whether fallback categories were used */
    fallbackUsed: boolean;
  };
  /** Enhanced prompt construction metadata */
  promptMetadata?: {
    /** Template used for construction */
    templateUsed: string;
    /** Whether truncation was applied */
    wasTruncated: boolean;
    /** Original prompt length */
    originalLength: number;
    /** Final prompt length */
    finalLength: number;
    /** Variables replaced count */
    variablesReplaced: number;
    /** Context formatting applied */
    contextFormatting: string;
    /** Complexity level used */
    complexityLevel: string;
  };
}

/**
 * Parsed classification result interface
 */
export interface ParsedClassificationResult {
  /** Identified category ID */
  categoryId: number;
  /** Category name */
  categoryName: string;
  /** Confidence score (0-1) */
  confidence: number;
  /** Raw response text */
  rawResponse: string;
  /** Parsing method used */
  parsingMethod: string;
}

/**
 * Enhanced category formatting options
 */
export interface CategoryFormattingOptions {
  /** Include detailed descriptions for categories */
  includeDescriptions: boolean;
  /** Include category metadata (validation info, etc.) */
  includeMetadata: boolean;
  /** Format style for categories in prompt */
  formatStyle: 'standard' | 'detailed' | 'compact';
  /** Maximum description length per category */
  maxDescriptionLength: number;
}

/**
 * Template definition for prompt construction
 */
interface PromptTemplate {
  /** Template name */
  name: string;
  /** Template content with variables */
  content: string;
  /** Default variables */
  defaults: Record<string, string>;
  /** Description of template usage */
  description: string;
}

/**
 * Prompt Engineering Service for Classification
 */
export class PromptEngineeringService {
  private readonly categoryLoader: CategoryLoader;
  private readonly logger: CategoryLogger;
  
  // Enhanced caching for formatted categories
  private formattedCategoriesCache: string | null = null;
  private categoriesCacheTimestamp: number = 0;
  private readonly categoriesCacheTTL: number = 2 * 60 * 1000; // 2 minutes
  
  // Template caching for enhanced prompt construction
  private templateCache: Map<string, PromptTemplate> = new Map();
  private templateCacheTimestamp: number = 0;
  private readonly templateCacheTTL: number = 5 * 60 * 1000; // 5 minutes
  
  // Performance tracking
  private promptGenerationCount: number = 0;
  private cacheHitCount: number = 0;
  private categoryRefreshCount: number = 0;
  private templateUsageCount: Map<string, number> = new Map();

  constructor(categoryLoader?: CategoryLoader, logger?: CategoryLogger) {
    this.categoryLoader = categoryLoader || CategoryLoader.getInstance();
    this.logger = logger || CategoryLogger.getInstance();
    
    this.initializeTemplates();
    
    this.logger.info('PromptEngineeringService initialized with enhanced template system', 'Constructor', {
      categoryLoaderInstance: !!categoryLoader,
      loggerInstance: !!logger,
      templatesInitialized: this.templateCache.size
    });
  }

  /**
   * Initialize default prompt templates
   */
  private initializeTemplates(): void {
    const templates: PromptTemplate[] = [
      {
        name: 'standard',
        content: `Please classify this user prompt:

"{{promptText}}"{{#context}}

Additional context: {{context}}{{/context}}{{#metadata}}

Metadata: {{metadata}}{{/metadata}}

Classification:`,
        defaults: {
          promptText: '',
          context: '',
          metadata: ''
        },
        description: 'Standard classification template with basic structure'
      },
      {
        name: 'detailed',
        content: `I need you to carefully analyze and classify the following user prompt based on its primary intent and content focus.

User Prompt: "{{promptText}}"{{#context}}

Context Information: {{context}}{{/context}}{{#intent}}

Apparent Intent: {{intent}}{{/intent}}{{#source}}

Source: {{source}}{{/source}}

Please provide your classification using the exact format specified in the system instructions.

Classification:`,
        defaults: {
          promptText: '',
          context: '',
          intent: '',
          source: ''
        },
        description: 'Detailed template with enhanced context and intent analysis'
      },
      {
        name: 'concise',
        content: `Classify: "{{promptText}}"{{#context}} (Context: {{context}}){{/context}}

Category:`,
        defaults: {
          promptText: '',
          context: ''
        },
        description: 'Concise template for simple, direct classification'
      },
      {
        name: 'technical',
        content: `Technical Analysis Required:

INPUT: "{{promptText}}"{{#context}}
CONTEXT: {{context}}{{/context}}{{#priority}}
PRIORITY: {{priority}}{{/priority}}

Please analyze the technical domain and classify according to the predefined categories.

OUTPUT:`,
        defaults: {
          promptText: '',
          context: '',
          priority: 'medium'
        },
        description: 'Technical template for complex, domain-specific prompts'
      }
    ];

    this.templateCache.clear();
    templates.forEach(template => {
      this.templateCache.set(template.name, template);
    });
    
    this.templateCacheTimestamp = Date.now();
    
    this.logger.debug('Prompt templates initialized', 'InitializeTemplates', {
      templateCount: templates.length,
      templateNames: templates.map(t => t.name)
    });
  }

  /**
   * Generate the system prompt template for classification with enhanced category integration
   * 
   * This prompt is optimized for ministral-3b and emphasizes:
   * - Clear role definition as a classifier
   * - Specific output format requirements
   * - Single-category selection mandate
   * - Edge case handling instructions
   * - Dynamic category loading with health checks
   */
  public generateSystemPrompt(options?: Partial<CategoryFormattingOptions>): string {
    const startTime = performance.now();
    
    try {
      // Validate CategoryLoader health before proceeding
      const loaderState = this.categoryLoader.validateState();
      if (!loaderState.isValid) {
        this.logger.warn('CategoryLoader state issues detected', 'GenerateSystemPrompt', {
          issues: loaderState.issues,
          recommendations: loaderState.recommendations
        });
      }

      const categories = this.getValidatedCategories();
      const formattingOptions: CategoryFormattingOptions = {
        includeDescriptions: true,
        includeMetadata: false,
        formatStyle: 'detailed',
        maxDescriptionLength: 150,
        ...options
      };
      
      const formattedCategories = this.getFormattedCategories(categories, formattingOptions);
      
      const systemPrompt = `You are an expert prompt classifier. Your sole responsibility is to analyze user prompts and classify them into exactly one of the predefined categories.

CRITICAL INSTRUCTIONS:
1. You MUST respond with exactly one category
2. You MUST use the exact format specified below
3. You MUST choose the most specific and accurate category
4. If uncertain, choose the closest match rather than "Other"
5. Only use "Other" for prompts that truly don't fit any category

CLASSIFICATION CATEGORIES:
${formattedCategories}

OUTPUT FORMAT (MANDATORY):
Respond with ONLY this exact format:
Category: [NUMBER]. [NAME]

EXAMPLES:
- For a coding question: "Category: 3. Programming"
- For a creative writing request: "Category: 2. Writing"
- For a math problem: "Category: 4. Math"

EDGE CASE HANDLING:
- Multi-topic prompts: Choose the PRIMARY focus
- Ambiguous prompts: Select the most likely intent
- Very short prompts: Infer from context and keywords
- Mixed content: Prioritize the main request/question

Remember: Always respond in the exact format "Category: [NUMBER]. [NAME]" with nothing else.`;

      this.promptGenerationCount++;
      const generationTime = performance.now() - startTime;
      
      this.logger.debug('System prompt generated successfully', 'GenerateSystemPrompt', {
        promptLength: systemPrompt.length,
        categoryCount: categories.length,
        generationTime: `${generationTime.toFixed(2)}ms`,
        fromCache: this.formattedCategoriesCache !== null
      });

      return systemPrompt;
    } catch (error) {
      this.logger.error(
        'Failed to generate system prompt',
        'GenerateSystemPrompt',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Enhanced user prompt generation with template system and advanced features
   * 
   * Constructs the final user prompt using configurable templates with:
   * - Variable replacement
   * - Intelligent truncation
   * - Context formatting
   * - Complexity-based strategies
   */
  public generateUserPrompt(request: ClassificationPromptRequest, options?: UserPromptOptions): string {
    const startTime = performance.now();
    
    try {
      // Handle forced refresh if requested
      if (request.forceRefresh) {
        this.refreshCategories();
      }

      // Determine template and options
      const templateName = options?.template || this.selectTemplateByComplexity(request.complexityLevel);
      const template = this.getTemplate(templateName);
      
      // Sanitize the input prompt
      const sanitizedPrompt = this.sanitizeUserInput(request.promptText);
      
      // Apply intelligent truncation
      const truncationStrategy = options?.truncationStrategy || 'intelligent';
      const maxLength = request.maxLength || this.getMaxPromptLength();
      const processedPrompt = this.applyTruncation(sanitizedPrompt, maxLength, truncationStrategy, request.context);
      
      // Prepare template variables
      const templateVariables = this.prepareTemplateVariables(request, processedPrompt, options);
      
      // Apply template with variable replacement
      const userPrompt = this.applyTemplate(template, templateVariables);
      
      // Track template usage
      const currentCount = this.templateUsageCount.get(templateName) || 0;
      this.templateUsageCount.set(templateName, currentCount + 1);

      const generationTime = performance.now() - startTime;

      this.logger.debug('Enhanced user prompt generated', 'GenerateUserPrompt', {
        templateUsed: templateName,
        originalLength: request.promptText?.length || 0,
        sanitizedLength: sanitizedPrompt.length,
        finalLength: userPrompt.length,
        truncated: sanitizedPrompt.length !== processedPrompt.length,
        hasContext: !!request.context,
        complexityLevel: request.complexityLevel || 'medium',
        variablesCount: Object.keys(templateVariables).length,
        generationTime: `${generationTime.toFixed(2)}ms`
      });

      return userPrompt;
    } catch (error) {
      this.logger.error(
        'Failed to generate enhanced user prompt',
        'GenerateUserPrompt',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Generate complete classification prompts with enhanced metadata
   * 
   * Combines system and user prompts with comprehensive metadata and performance tracking
   */
  public generateClassificationPrompts(request: ClassificationPromptRequest, options?: UserPromptOptions): ClassificationPromptResponse {
    const startTime = performance.now();
    
    try {
      const systemPrompt = this.generateSystemPrompt();
      const userPrompt = this.generateUserPrompt(request, options);
      const availableCategories = this.getValidatedCategories();
      
      // Get category metadata from CategoryLoader
      const cacheInfo = this.categoryLoader.getCacheInfo();
      const diagnostics = this.categoryLoader.getDiagnostics();
      
      // Estimate tokens (rough approximation: 1 token ≈ 4 characters)
      const estimatedTokens = Math.ceil((systemPrompt.length + userPrompt.length) / 4);

      // Prepare enhanced prompt metadata
      const templateName = options?.template || this.selectTemplateByComplexity(request.complexityLevel);
      const originalLength = request.promptText.length;
      const sanitizedLength = this.sanitizeUserInput(request.promptText).length;
      const variablesCount = Object.keys(request.templateVariables || {}).length;

      const response: ClassificationPromptResponse = {
        systemPrompt,
        userPrompt,
        estimatedTokens,
        availableCategories,
        categoryMetadata: {
          totalCategories: availableCategories.length,
          fromCache: cacheInfo.isCacheValid,
          cacheAge: cacheInfo.cacheAge,
          fallbackUsed: diagnostics.data.fallbackUsed
        },
        promptMetadata: {
          templateUsed: templateName,
          wasTruncated: sanitizedLength !== originalLength,
          originalLength,
          finalLength: userPrompt.length,
          variablesReplaced: variablesCount,
          contextFormatting: options?.contextFormatting || 'inline',
          complexityLevel: request.complexityLevel || 'medium'
        }
      };

      const generationTime = performance.now() - startTime;
      this.logger.info('Enhanced classification prompts generated', 'GenerateClassificationPrompts', {
        systemPromptLength: systemPrompt.length,
        userPromptLength: userPrompt.length,
        estimatedTokens,
        categoryCount: availableCategories.length,
        generationTime: `${generationTime.toFixed(2)}ms`,
        metadata: response.categoryMetadata,
        promptMetadata: response.promptMetadata
      });

      return response;
    } catch (error) {
      this.logger.error(
        'Failed to generate enhanced classification prompts',
        'GenerateClassificationPrompts',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Select appropriate template based on complexity level
   */
  private selectTemplateByComplexity(complexityLevel?: string): string {
    switch (complexityLevel) {
      case 'simple':
        return 'concise';
      case 'complex':
        return 'detailed';
      case 'medium':
      default:
        return 'standard';
    }
  }

  /**
   * Get template from cache or initialize if needed
   */
  private getTemplate(templateName: string): PromptTemplate {
    // Check if template cache needs refresh
    if (Date.now() - this.templateCacheTimestamp > this.templateCacheTTL) {
      this.initializeTemplates();
    }

    const template = this.templateCache.get(templateName);
    if (!template) {
      this.logger.warn('Unknown template requested, falling back to standard', 'GetTemplate', {
        requestedTemplate: templateName,
        availableTemplates: Array.from(this.templateCache.keys())
      });
      return this.templateCache.get('standard')!;
    }

    return template;
  }

  /**
   * Prepare template variables from request and processed data
   */
  private prepareTemplateVariables(
    request: ClassificationPromptRequest, 
    processedPrompt: string, 
    options?: UserPromptOptions
  ): Record<string, string> {
    const variables: Record<string, string> = {
      promptText: processedPrompt,
      context: request.context || '',
      ...request.templateVariables || {}
    };

    // Add metadata variables if available
    if (request.metadata) {
      variables.source = request.metadata.source || '';
      variables.intent = request.metadata.intent || '';
      variables.priority = request.metadata.priority || 'medium';
      variables.metadata = JSON.stringify(request.metadata);
    }

    // Apply context type formatting
    if (request.context && request.contextType) {
      variables.context = this.formatContext(request.context, request.contextType);
    }

    // Apply defaults from variable replacement options
    if (options?.variableReplacement?.defaults) {
      Object.keys(options.variableReplacement.defaults).forEach(key => {
        if (!variables[key]) {
          variables[key] = options.variableReplacement!.defaults![key];
        }
      });
    }

    // Sanitize variables if requested
    if (options?.variableReplacement?.escapeVariables) {
      Object.keys(variables).forEach(key => {
        variables[key] = this.sanitizeUserInput(variables[key]);
      });
    }

    return variables;
  }

  /**
   * Apply template with variable replacement using simple mustache-like syntax
   */
  private applyTemplate(
    template: PromptTemplate, 
    variables: Record<string, string>
  ): string {
    let content = template.content;

    // Apply default values for missing variables
    const allVariables = { ...template.defaults, ...variables };

    // Simple mustache-like variable replacement
    // Replace {{variable}} patterns
    content = content.replace(/\{\{(\w+)\}\}/g, (match, variableName) => {
      return allVariables[variableName] || match;
    });

    // Handle conditional sections {{#variable}}...{{/variable}}
    content = content.replace(/\{\{#(\w+)\}\}(.*?)\{\{\/\1\}\}/gs, (_match: string, variableName: string, sectionContent: string) => {
      const variableValue = allVariables[variableName];
      if (variableValue && variableValue.trim()) {
        // Replace variables within the section
        return sectionContent.replace(/\{\{(\w+)\}\}/g, (innerMatch: string, innerVar: string) => {
          return allVariables[innerVar] || innerMatch;
        });
      }
      return '';
    });

    return content.trim();
  }

  /**
   * Apply intelligent truncation based on strategy
   */
  private applyTruncation(
    text: string, 
    maxLength: number, 
    strategy: string, 
    context?: string
  ): string {
    if (text.length <= maxLength) {
      return text;
    }

    switch (strategy) {
      case 'simple':
        return text.substring(0, maxLength) + '...';
        
      case 'preserve_context':
        return this.preserveContextTruncation(text, maxLength, context);
        
      case 'intelligent':
      default:
        return this.intelligentTruncation(text, maxLength);
    }
  }

  /**
   * Intelligent truncation that preserves sentence boundaries and key content
   */
  private intelligentTruncation(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }

    // Try to truncate at sentence boundaries
    const sentences = text.split(/[.!?]+/);
    let result = '';
    
    for (const sentence of sentences) {
      const withSentence = result + sentence + '.';
      if (withSentence.length > maxLength - 3) { // Reserve space for "..."
        break;
      }
      result = withSentence;
    }

    // If no complete sentences fit, truncate at word boundaries
    if (result.length < maxLength * 0.5) {
      const words = text.split(/\s+/);
      result = '';
      
      for (const word of words) {
        const withWord = result + (result ? ' ' : '') + word;
        if (withWord.length > maxLength - 3) {
          break;
        }
        result = withWord;
      }
    }

    return (result || text.substring(0, maxLength - 3)) + '...';
  }

  /**
   * Context-preserving truncation that maintains important contextual information
   */
  private preserveContextTruncation(text: string, maxLength: number, context?: string): string {
    if (text.length <= maxLength) {
      return text;
    }

    // If context is provided, prioritize preserving parts that relate to context
    if (context) {
      const contextKeywords = context.toLowerCase().split(/\s+/).filter(w => w.length > 3);
      const sentences = text.split(/[.!?]+/);
      
      // Score sentences based on context relevance
      const scoredSentences = sentences.map(sentence => {
        const score = contextKeywords.reduce((acc, keyword) => 
          acc + (sentence.toLowerCase().includes(keyword) ? 1 : 0), 0);
        return { sentence, score };
      }).sort((a, b) => b.score - a.score);

      // Build result with highest-scoring sentences
      let result = '';
      for (const { sentence } of scoredSentences) {
        const withSentence = result + (result ? '. ' : '') + sentence.trim();
        if (withSentence.length > maxLength - 3) {
          break;
        }
        result = withSentence;
      }

      if (result) {
        return result + '...';
      }
    }

    // Fallback to intelligent truncation
    return this.intelligentTruncation(text, maxLength);
  }

  /**
   * Format context based on context type
   */
  private formatContext(context: string, contextType: string): string {
    switch (contextType) {
      case 'technical':
        return `[TECHNICAL CONTEXT] ${context}`;
      case 'creative':
        return `[CREATIVE CONTEXT] ${context}`;
      case 'educational':
        return `[EDUCATIONAL CONTEXT] ${context}`;
      case 'general':
      default:
        return context;
    }
  }

  /**
   * Get validated categories with health checks
   */
  private getValidatedCategories(): Category[] {
    try {
      const categories = this.categoryLoader.getCategories();
      
      // Validate categories using CategoryLoader's validation
      const validation = this.categoryLoader.validateCategoriesData(categories);
      if (!validation.isValid) {
        this.logger.warn('Category validation issues detected', 'GetValidatedCategories', {
          errors: validation.errors,
          warnings: validation.warnings,
          summary: validation.summary
        });
      }
      
      return categories;
    } catch (error) {
      this.logger.error(
        'Failed to get validated categories',
        'GetValidatedCategories',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      
      // Attempt recovery
      const recovered = this.categoryLoader.attemptRecovery();
      if (recovered) {
        this.logger.info('CategoryLoader recovery successful', 'GetValidatedCategories');
        return this.categoryLoader.getCategories();
      }
      
      throw error;
    }
  }

  /**
   * Get formatted categories with intelligent caching
   */
  private getFormattedCategories(categories: Category[], options: CategoryFormattingOptions): string {
    const currentTime = Date.now();
    
    // Check if cached version is still valid
    if (this.formattedCategoriesCache && 
        (currentTime - this.categoriesCacheTimestamp) < this.categoriesCacheTTL) {
      this.cacheHitCount++;
      this.logger.debug('Using cached formatted categories', 'GetFormattedCategories', {
        cacheAge: currentTime - this.categoriesCacheTimestamp,
        cacheHits: this.cacheHitCount
      });
      return this.formattedCategoriesCache;
    }
    
    // Generate fresh formatted categories
    const formatted = this.formatCategoriesForPrompt(categories, options);
    
    // Update cache
    this.formattedCategoriesCache = formatted;
    this.categoriesCacheTimestamp = currentTime;
    
    this.logger.debug('Generated fresh formatted categories', 'GetFormattedCategories', {
      categoryCount: categories.length,
      formattedLength: formatted.length,
      options
    });
    
    return formatted;
  }

  /**
   * Enhanced category formatting for inclusion in the system prompt
   */
  private formatCategoriesForPrompt(categories: Category[], options: CategoryFormattingOptions): string {
    return categories.map(category => {
      let formatted = `${category.id}. ${category.name}`;
      
      if (options.includeDescriptions) {
        const description = this.getCategoryDescription(category.name);
        const truncatedDescription = description.length > options.maxDescriptionLength
          ? description.substring(0, options.maxDescriptionLength) + '...'
          : description;
        
        formatted += `: ${truncatedDescription}`;
      }
      
      if (options.includeMetadata && category.description) {
        formatted += ` [${category.description}]`;
      }
      
      return formatted;
    }).join('\n');
  }

  /**
   * Refresh categories and invalidate caches
   */
  public refreshCategories(): void {
    try {
      this.logger.info('Refreshing categories', 'RefreshCategories');
      
      // Refresh CategoryLoader
      this.categoryLoader.refresh();
      
      // Invalidate local caches
      this.formattedCategoriesCache = null;
      this.categoriesCacheTimestamp = 0;
      this.categoryRefreshCount++;
      
      this.logger.info('Categories refreshed successfully', 'RefreshCategories', {
        refreshCount: this.categoryRefreshCount
      });
    } catch (error) {
      this.logger.error(
        'Failed to refresh categories',
        'RefreshCategories',
        { error: error instanceof Error ? error.message : String(error) },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Get service diagnostics and performance metrics
   */
  public getDiagnostics() {
    const categoryDiagnostics = this.categoryLoader.getDiagnostics();
    const cacheInfo = this.categoryLoader.getCacheInfo();
    
    return {
      promptEngineering: {
        promptGenerationCount: this.promptGenerationCount,
        cacheHitCount: this.cacheHitCount,
        categoryRefreshCount: this.categoryRefreshCount,
        formattedCategoriesCached: this.formattedCategoriesCache !== null,
        cacheAge: this.formattedCategoriesCache ? Date.now() - this.categoriesCacheTimestamp : 0,
        cacheTTL: this.categoriesCacheTTL
      },
      categoryLoader: categoryDiagnostics,
      categoryCache: cacheInfo
    };
  }

  /**
   * Get descriptive text for each category to improve classification accuracy
   */
  private getCategoryDescription(categoryName: string): string {
    const descriptions: Record<string, string> = {
      'Roleplay': 'Requests to simulate characters, scenarios, personas, or interactive conversations',
      'Writing': 'Creative writing, essays, stories, articles, content creation, or literary assistance',
      'Programming': 'Coding questions, debugging, software development, algorithms, or technical programming help',
      'Math': 'Mathematical problems, calculations, equations, statistics, or numerical analysis',
      'Science': 'Scientific inquiries, research questions, explanations of natural phenomena, or academic science topics',
      'History': 'Questions about historical events, figures, periods, or historical analysis',
      'Marketing': 'Advertising strategies, branding, market analysis, promotional content, or business marketing',
      'Translation': 'Converting text between languages or language-related assistance',
      'Legal': 'Legal questions, regulations, law interpretation, or legal advice (informational)',
      'Finance': 'Money management, investments, financial planning, economics, or monetary topics',
      'Health': 'Medical questions, wellness advice, healthcare topics, or health-related information',
      'Trivia': 'General knowledge questions, facts, quiz-style inquiries, or interesting information',
      'SEO': 'Search engine optimization, web ranking, keywords, or online visibility strategies',
      'Social Media': 'Content for social platforms, social media strategy, or online engagement',
      'Other': 'Anything that doesn\'t clearly fit into the above specific categories'
    };

    return descriptions[categoryName] || 'General category for unspecified topics';
  }

  /**
   * Sanitize user input to prevent prompt injection and ensure safety
   */
  private sanitizeUserInput(input: string): string {
    if (!input || typeof input !== 'string') {
      return '';
    }

    return input
      // Remove or escape potential prompt injection attempts
      .replace(/```/g, '`\\`\\`') // Escape code blocks properly
      .replace(/(^|\n)\s*System:/gi, '$1User mentioned system:') // Prevent system override
      .replace(/(^|\n)\s*Assistant:/gi, '$1User mentioned assistant:') // Prevent assistant override
      .replace(/(^|\n)\s*Human:/gi, '$1User mentioned human:') // Prevent human override
      // Trim excessive whitespace
      .replace(/\s+/g, ' ')
      .trim()
      // Limit length as additional safety
      .substring(0, 5000);
  }

  /**
   * Get the maximum recommended prompt length for ministral-3b
   */
  public getMaxPromptLength(): number {
    // Conservative limit considering model context window
    return 3000; // characters
  }

  /**
   * Parse classification response from the model with enhanced validation
   * 
   * Implements multiple parsing strategies with confidence scoring and category validation
   */
  public parseClassificationResponse(response: string): ParsedClassificationResult {
    const startTime = performance.now();
    
    try {
      const availableCategories = this.getValidatedCategories();
      const sanitizedResponse = response.trim();

      this.logger.debug('Parsing classification response', 'ParseClassificationResponse', {
        responseLength: response.length,
        sanitizedLength: sanitizedResponse.length,
        availableCategoryCount: availableCategories.length
      });

      // Strategy 1: Exact format match "Category: X. Name"
      const exactMatch = sanitizedResponse.match(/^Category:\s*(\d+)\.\s*(.+)$/i);
      if (exactMatch) {
        const categoryId = parseInt(exactMatch[1], 10);
        const categoryName = exactMatch[2].trim();
        
        if (this.isValidCategory(categoryId, categoryName, availableCategories)) {
          const result = {
            categoryId,
            categoryName,
            confidence: 0.95, // High confidence for exact format
            rawResponse: response,
            parsingMethod: 'exact_format'
          };
          
          this.logParsingSuccess(result, performance.now() - startTime);
          return result;
        }
      }

      // Strategy 2: Number and name pattern (flexible)
      const numberNameMatch = sanitizedResponse.match(/(\d+)[\.\:\s]*([a-zA-Z\s]+)/);
      if (numberNameMatch) {
        const categoryId = parseInt(numberNameMatch[1], 10);
        const categoryName = numberNameMatch[2].trim();
        
        if (this.isValidCategory(categoryId, categoryName, availableCategories)) {
          const result = {
            categoryId,
            categoryName,
            confidence: 0.80, // Good confidence for pattern match
            rawResponse: response,
            parsingMethod: 'number_name_pattern'
          };
          
          this.logParsingSuccess(result, performance.now() - startTime);
          return result;
        }
      }

      // Strategy 3: Category name match (case-insensitive)
      for (const category of availableCategories) {
        const regex = new RegExp(`\\b${category.name}\\b`, 'i');
        if (regex.test(sanitizedResponse)) {
          const result = {
            categoryId: category.id,
            categoryName: category.name,
            confidence: 0.70, // Moderate confidence for name match
            rawResponse: response,
            parsingMethod: 'name_match'
          };
          
          this.logParsingSuccess(result, performance.now() - startTime);
          return result;
        }
      }

      // Strategy 4: Keyword matching for fallback
      const keywordMatch = this.matchByKeywords(sanitizedResponse, availableCategories);
      if (keywordMatch) {
        const result = {
          categoryId: keywordMatch.id,
          categoryName: keywordMatch.name,
          confidence: 0.60, // Lower confidence for keyword match
          rawResponse: response,
          parsingMethod: 'keyword_match'
        };
        
        this.logParsingSuccess(result, performance.now() - startTime);
        return result;
      }

      // Strategy 5: Default fallback to "Other"
      const otherCategory = this.categoryLoader.getDefaultCategory();
      
      this.logger.warn('Failed to parse classification response, defaulting to Other', 'ParseClassificationResponse', {
        response: sanitizedResponse,
        availableCategories: availableCategories.length,
        parsingTime: `${(performance.now() - startTime).toFixed(2)}ms`
      });

      return {
        categoryId: otherCategory.id,
        categoryName: otherCategory.name,
        confidence: 0.30, // Low confidence for fallback
        rawResponse: response,
        parsingMethod: 'fallback_other'
      };
    } catch (error) {
      this.logger.error(
        'Failed to parse classification response',
        'ParseClassificationResponse',
        { 
          response: response.substring(0, 200),
          error: error instanceof Error ? error.message : String(error) 
        },
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Validate if a category ID and name match available categories
   */
  private isValidCategory(categoryId: number, categoryName: string, availableCategories: Category[]): boolean {
    const category = availableCategories.find(cat => cat.id === categoryId);
    if (!category) {
      return false;
    }
    
    // Allow some flexibility in name matching (case-insensitive, trimmed)
    const normalizedExpected = category.name.toLowerCase().trim();
    const normalizedProvided = categoryName.toLowerCase().trim();
    
    return normalizedExpected === normalizedProvided;
  }

  /**
   * Log successful parsing with performance metrics
   */
  private logParsingSuccess(result: ParsedClassificationResult, parsingTime: number): void {
    this.logger.debug('Classification parsing successful', 'ParseClassificationResponse', {
      categoryId: result.categoryId,
      categoryName: result.categoryName,
      confidence: result.confidence,
      parsingMethod: result.parsingMethod,
      parsingTime: `${parsingTime.toFixed(2)}ms`
    });
  }

  /**
   * Match response against category keywords for fallback classification
   */
  private matchByKeywords(response: string, availableCategories: Category[]): Category | null {
    const lowerResponse = response.toLowerCase();
    
    // Define keyword patterns for each category
    const categoryKeywords: Record<string, string[]> = {
      'Programming': ['code', 'coding', 'program', 'programming', 'software', 'development', 'algorithm', 'function', 'debug', 'debugging', 'script', 'javascript', 'python', 'java', 'typescript', 'react', 'api', 'database', 'sql'],
      'Writing': ['write', 'writing', 'story', 'essay', 'article', 'content', 'creative', 'novel', 'paragraph', 'draft', 'edit', 'editing', 'blog', 'text', 'document', 'letter'],
      'Math': ['math', 'mathematics', 'calculate', 'calculation', 'equation', 'formula', 'solve', 'number', 'algebra', 'geometry', 'statistics', 'probability', 'derivative', 'integral'],
      'Science': ['science', 'scientific', 'research', 'experiment', 'hypothesis', 'theory', 'physics', 'chemistry', 'biology', 'astronomy', 'geology', 'laboratory', 'analysis'],
      'History': ['history', 'historical', 'ancient', 'medieval', 'war', 'civilization', 'empire', 'revolution', 'timeline', 'century', 'era', 'dynasty', 'cultural'],
      'Marketing': ['marketing', 'advertising', 'brand', 'branding', 'promotion', 'campaign', 'sales', 'customer', 'market', 'strategy', 'business', 'product', 'service'],
      'Translation': ['translate', 'translation', 'language', 'foreign', 'spanish', 'french', 'german', 'chinese', 'japanese', 'english', 'convert', 'interpret'],
      'Legal': ['legal', 'law', 'lawyer', 'attorney', 'court', 'lawsuit', 'contract', 'regulation', 'compliance', 'rights', 'justice', 'statute', 'legislation'],
      'Finance': ['finance', 'financial', 'money', 'investment', 'budget', 'loan', 'bank', 'banking', 'stock', 'market', 'trading', 'insurance', 'tax', 'accounting'],
      'Health': ['health', 'medical', 'doctor', 'medicine', 'treatment', 'symptoms', 'diagnosis', 'therapy', 'hospital', 'patient', 'wellness', 'fitness', 'nutrition'],
      'Trivia': ['trivia', 'quiz', 'fact', 'facts', 'knowledge', 'question', 'answer', 'curious', 'interesting', 'did you know', 'tell me about', 'explain'],
      'SEO': ['seo', 'search engine', 'optimization', 'keywords', 'ranking', 'google', 'website', 'traffic', 'organic', 'serp', 'backlinks', 'meta'],
      'Social Media': ['social media', 'facebook', 'twitter', 'instagram', 'linkedin', 'tiktok', 'youtube', 'post', 'share', 'followers', 'engagement', 'viral'],
      'Roleplay': ['roleplay', 'role play', 'character', 'persona', 'pretend', 'act as', 'simulate', 'scenario', 'dialogue', 'conversation', 'imaginary', 'fantasy']
    };

    // Score each category based on keyword matches
    const categoryScores = new Map<string, number>();
    
    for (const [categoryName, keywords] of Object.entries(categoryKeywords)) {
      let score = 0;
      keywords.forEach(keyword => {
        if (lowerResponse.includes(keyword)) {
          score += 1;
        }
      });
      
      if (score > 0) {
        categoryScores.set(categoryName, score);
      }
    }

    // Find the category with the highest score
    if (categoryScores.size === 0) {
      return null;
    }

    const topCategory = Array.from(categoryScores.entries())
      .sort(([,a], [,b]) => b - a)[0];
    
    const [topCategoryName] = topCategory;
    
    // Find the corresponding category object
    return availableCategories.find(cat => 
      cat.name.toLowerCase() === topCategoryName.toLowerCase()
    ) || null;
  }

  /**
   * Estimate token count for text (rough approximation)
   */
  public estimateTokenCount(text: string): number {
    if (!text || typeof text !== 'string') {
      return 0;
    }
    
    // Rough estimation: 1 token ≈ 4 characters for most models
    return Math.ceil(text.length / 4);
  }
} 